import { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Gantt } from "dhtmlx-gantt";
import "dhtmlx-gantt/codebase/dhtmlxgantt.css";
import "./OssDashboardTimeline.css";
import { buildGanttTasks } from "../../utils/timelineUtils";

const taskClass = {
  NORMAL: "oss-gantt-risk-normal",
  ATTENTION: "oss-gantt-risk-attention",
  HIGH: "oss-gantt-risk-high",
  OVERDUE: "oss-gantt-risk-overdue",
};

const riskChipClass = {
  NORMAL: "oss-gantt-risk-chip-normal",
  ATTENTION: "oss-gantt-risk-chip-attention",
  HIGH: "oss-gantt-risk-chip-high",
  OVERDUE: "oss-gantt-risk-chip-overdue",
};

const riskSortRank = {
  NORMAL: 1,
  ATTENTION: 2,
  HIGH: 3,
  OVERDUE: 4,
};

const getMonthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const getNextMonthStart = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 1);

const getMonthCount = (start, end) =>
  Math.max(1, (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth());

const zoomOptions = [
  { key: "year", label: "연" },
  { key: "quarter", label: "분기" },
  { key: "month", label: "월" },
];

const escapeHtml = (value) =>
  String(value ?? "-")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const sortableLabel = (key, label, sortState) => {
  const active = sortState?.key === key;
  const mark = active ? (sortState.direction === "asc" ? "▲" : "▼") : "";
  return `<span class="oss-gantt-sort-header" data-oss-gantt-sort="${key}">${escapeHtml(label)}<span class="oss-gantt-sort-mark">${mark}</span></span>`;
};

const getGanttColumns = (sortState) => [
  {
    name: "customerName",
    label: sortableLabel("customerName", "거래처", sortState),
    align: "center",
    width: 104,
    template: (task) => `<span class="oss-gantt-grid-main" title="${escapeHtml(task.customerName)}">${escapeHtml(task.customerName)}</span>`,
  },
  {
    name: "itemName",
    label: sortableLabel("itemName", "품목", sortState),
    align: "center",
    width: 118,
    template: (task) => `<span class="oss-gantt-grid-sub" title="${escapeHtml(task.itemName || task.text)}">${escapeHtml(task.itemName || task.text)}</span>`,
  },
  {
    name: "startShortLabel",
    label: sortableLabel("startDate", "착수", sortState),
    align: "center",
    width: 64,
    template: (task) => `<span class="oss-gantt-grid-date">${escapeHtml(task.startShortLabel)}</span>`,
  },
  {
    name: "dueShortLabel",
    label: sortableLabel("dueDate", "납기", sortState),
    align: "center",
    width: 64,
    template: (task) => `<span class="oss-gantt-grid-date">${escapeHtml(task.dueShortLabel)}</span>`,
  },
  {
    name: "riskLabel",
    label: sortableLabel("riskLevel", "위험", sortState),
    align: "center",
    width: 60,
    template: (task) =>
      `<span class="oss-gantt-risk-chip ${riskChipClass[task.riskLevel] || riskChipClass.NORMAL}">${escapeHtml(task.riskLabel)}</span>`,
  },
];

const getSortValue = (task, key) => {
  switch (key) {
    case "customerName":
      return task.customerName || "";
    case "itemName":
      return task.itemName || task.text || "";
    case "dueDate":
      return task.end_date?.getTime?.() || 0;
    case "riskLevel":
      return riskSortRank[task.riskLevel] || 0;
    case "startDate":
    default:
      return task.start_date?.getTime?.() || 0;
  }
};

const sortGanttTasks = (tasks, sortState) => {
  const direction = sortState?.direction === "desc" ? -1 : 1;
  const key = sortState?.key || "startDate";

  return [...tasks].sort((left, right) => {
    const leftValue = getSortValue(left, key);
    const rightValue = getSortValue(right, key);
    const primary =
      typeof leftValue === "number" && typeof rightValue === "number"
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), "ko");

    if (primary !== 0) return primary * direction;

    return (
      (left.start_date?.getTime?.() || 0) - (right.start_date?.getTime?.() || 0) ||
      (left.end_date?.getTime?.() || 0) - (right.end_date?.getTime?.() || 0) ||
      String(left.orderNo || "").localeCompare(String(right.orderNo || ""), "ko")
    );
  });
};

const getGanttRange = (tasks = []) => {
  const parentTasks = tasks.filter((task) => task.type === "project" && task.start_date && task.end_date);
  const source = parentTasks.length ? parentTasks : tasks.filter((task) => task.start_date && task.end_date);
  const starts = source.map((task) => task.start_date).sort((a, b) => a - b);
  const ends = source.map((task) => task.end_date).sort((a, b) => b - a);
  const start = starts[0] ? getMonthStart(starts[0]) : getMonthStart(new Date());
  const end = ends[0] ? getNextMonthStart(ends[0]) : getNextMonthStart(new Date());
  return { start, end, monthCount: getMonthCount(start, end) };
};

const getScaleUnitCount = (range, zoomLevel) => {
  if (zoomLevel === "year") {
    return Math.max(1, range.end.getFullYear() - range.start.getFullYear() + 1);
  }
  if (zoomLevel === "quarter") return Math.max(1, Math.ceil(range.monthCount / 3));
  return range.monthCount;
};

const getScales = (zoomLevel) => {
  if (zoomLevel === "year") {
    return [{ unit: "year", step: 1, format: "%Y년" }];
  }
  if (zoomLevel === "quarter") {
    return [
      { unit: "year", step: 1, format: "%Y년" },
      { unit: "month", step: 3, format: (date) => `${Math.floor(date.getMonth() / 3) + 1}분기` },
    ];
  }
  return [
    { unit: "year", step: 1, format: "%Y년" },
    { unit: "month", step: 1, format: "%m월" },
  ];
};

const isHolidayCell = (date) => date.getDay() === 0 || date.getDay() === 6;

const applyResponsiveScale = (gantt, container, tasks = [], zoomLevel = "month") => {
  const width = container?.clientWidth || 1200;
  const height = container?.clientHeight || 560;
  const gridWidth = Math.min(430, Math.max(360, Math.floor(width * 0.27)));
  const timelineWidth = Math.max(320, width - gridWidth - 2);
  const range = getGanttRange(tasks);
  const taskCount = Math.max(1, tasks.length);
  const availableRowHeight = Math.floor((height - 92) / taskCount);
  const rowHeight = Math.min(48, Math.max(38, availableRowHeight));
  const scaleUnitCount = getScaleUnitCount(range, zoomLevel);

  gantt.config.grid_width = gridWidth;
  gantt.config.min_column_width = Math.max(34, Math.floor(timelineWidth / scaleUnitCount));
  gantt.config.start_date = range.start;
  gantt.config.end_date = range.end;
  gantt.config.row_height = rowHeight;
  gantt.config.bar_height = Math.min(23, Math.max(20, rowHeight - 20));
  gantt.config.scales = getScales(zoomLevel);
  gantt.config.scale_height = zoomLevel === "year" ? 34 : 52;
};

export const OssDashboardGanttView = ({ rows = [], loading, onOpenOrder }) => {
  const containerRef = useRef(null);
  const ganttRef = useRef(null);
  const dataRef = useRef([]);
  const openOrderRef = useRef(onOpenOrder);
  const [sortState, setSortState] = useState({ key: "startDate", direction: "asc" });
  const [zoomLevel, setZoomLevel] = useState("month");
  const sortStateRef = useRef(sortState);
  const zoomLevelRef = useRef(zoomLevel);

  useEffect(() => {
    openOrderRef.current = onOpenOrder;
  }, [onOpenOrder]);

  useEffect(() => {
    sortStateRef.current = sortState;
  }, [sortState]);

  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const gantt = Gantt.getGanttInstance();
    const container = containerRef.current;
    ganttRef.current = gantt;
    gantt.config.readonly = true;
    gantt.config.drag_move = false;
    gantt.config.drag_resize = false;
    gantt.config.drag_progress = false;
    gantt.config.show_links = false;
    gantt.config.autosize = false;
    gantt.config.autoscroll = false;
    gantt.config.smart_rendering = true;
    gantt.config.smart_scales = true;
    gantt.config.show_task_cells = false;
    gantt.config.scroll_size = 0;
    gantt.config.row_height = 50;
    gantt.config.bar_height = 24;
    gantt.config.open_tree_initially = false;
    gantt.config.open_split_tasks = false;
    gantt.plugins?.({ tooltip: true });
    gantt.config.columns = getGanttColumns(sortStateRef.current);
    gantt.config.scales = getScales(zoomLevelRef.current);
    gantt.templates.task_class = (_start, _end, task) => taskClass[task.riskLevel] || taskClass.NORMAL;
    gantt.templates.scale_cell_class = (date) => (isHolidayCell(date) ? "oss-gantt-holiday-scale" : "");
    gantt.templates.timeline_cell_class = (_task, date) => (isHolidayCell(date) ? "oss-gantt-holiday-cell" : "");
    gantt.templates.task_text = (_start, _end, task) => task.scheduleLabel || task.riskLabel || "";
    gantt.templates.tooltip_text = (_start, _end, task) => {
      const title = `${task.orderNo || "-"} · ${task.itemName || task.text}`;
      return `
        <div class="oss-gantt-tooltip">
          <div class="oss-gantt-tooltip-title">${escapeHtml(title)}</div>
          <div class="oss-gantt-tooltip-grid">
            <span>주문번호</span><strong>${escapeHtml(task.orderNo)}</strong>
            <span>거래처</span><strong>${escapeHtml(task.customerName)}</strong>
            <span>품목</span><strong>${escapeHtml(task.itemName)}</strong>
            <span>착수</span><strong>${escapeHtml(task.startLabel)}</strong>
            <span>납기</span><strong>${escapeHtml(task.dueLabel)}</strong>
            <span>여유일</span><strong>${escapeHtml(task.availableMarginDays === null || task.availableMarginDays === undefined ? "-" : `${task.availableMarginDays}일`)}</strong>
            <span>일정</span><strong>${escapeHtml(task.scheduleLabel)}</strong>
            <span>위험</span><strong>${escapeHtml(task.riskLabel)}</strong>
          </div>
        </div>
      `;
    };

    const eventId = gantt.attachEvent("onTaskDblClick", (id) => {
      const task = gantt.getTask(id);
      if (task?.orderId) openOrderRef.current?.(task.orderId);
      return false;
    });

    const handleHeaderClick = (event) => {
      const target = event.target.closest?.("[data-oss-gantt-sort]");
      if (!target || !container.contains(target)) return;
      const key = target.getAttribute("data-oss-gantt-sort");
      if (!key) return;
      setSortState((current) => ({
        key,
        direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
      }));
    };
    container.addEventListener("click", handleHeaderClick);

    applyResponsiveScale(gantt, container, dataRef.current, zoomLevelRef.current);
    gantt.init(container);
    gantt.setSizes();

    let resizeFrame = null;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        const currentGantt = ganttRef.current;
        if (!currentGantt) return;
        applyResponsiveScale(currentGantt, container, dataRef.current, zoomLevelRef.current);
        currentGantt.render();
        currentGantt.setSizes();
      });
    });
    resizeObserver.observe(container);

    return () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeObserver.disconnect();
      container.removeEventListener("click", handleHeaderClick);
      gantt.detachEvent(eventId);
      gantt.destructor();
      ganttRef.current = null;
    };
  }, []);

  useEffect(() => {
    const gantt = ganttRef.current;
    if (!gantt) return;
    const data = sortGanttTasks(buildGanttTasks(rows), sortState);
    dataRef.current = data;
    gantt.config.columns = getGanttColumns(sortState);
    applyResponsiveScale(gantt, containerRef.current, data, zoomLevel);
    gantt.clearAll();
    gantt.parse({ data, links: [] });
    gantt.render();
    gantt.setSizes();
  }, [rows, sortState, zoomLevel]);

  return (
    <Stack spacing={1} sx={{ position: "relative", flex: 1, minHeight: 0, height: "100%", maxHeight: "100%", overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0 }}>
        <Typography sx={{ fontSize: 12.5, color: "#64748b", fontWeight: 700 }}>
          보기 단위를 바꾸면 장기 흐름을 더 크게 또는 더 촘촘하게 확인할 수 있습니다.
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {zoomOptions.map((option) => (
            <Button
              key={option.key}
              size="small"
              variant={zoomLevel === option.key ? "contained" : "outlined"}
              onClick={() => setZoomLevel(option.key)}
              sx={{ minWidth: 58, height: 32 }}
            >
              {option.label}
            </Button>
          ))}
        </Stack>
      </Stack>
      <Box sx={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Box ref={containerRef} className="oss-dashboard-gantt" />
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(255,255,255,0.6)",
          }}
        >
          <CircularProgress size={30} />
        </Box>
      )}
      </Box>
    </Stack>
  );
};
